<script setup>
  import {ref} from "vue";

  import ComponentAlert from "@components/Alerts/Alert.vue";
  import ComponentButton from "@components/Buttons/Button.vue";
  import PatientinputComponentScannerDialog from "@pages/Patientinput/Components/Scanners/ScannerDialog.vue";
  import {MedButton} from "@pharma4u/medicheck-components";
  import Pzn from "@utils/Pzn.js";
  import {Symbology} from "scandit-web-datacapture-barcode";

  defineOptions({
    name: "PatientinputComponentPznScannerDialog",
  });

  const props = defineProps({
    form: {
      type: Object,
      required: true,
    },
  });

  defineEmits(["ready"]);

  const refScannerContainer = ref(null);
  const paused = ref(false);
  const scannedPzn = ref(null);

  const formats = [Symbology.Code39, Symbology.Code128];

  const open = () => {
    paused.value = false;
    scannedPzn.value = null;
    refScannerContainer.value.open();
  };

  const close = () => {
    refScannerContainer.value.close();
  };

  const onDetect = (detectedCodes) => {
    if (detectedCodes.length === 1 && Pzn.validatePzn(detectedCodes[0]._data)) {
      if (
        props.form.medication.pzns.some(
          (pzn) => Number.parseInt(pzn) === Number.parseInt(Pzn.getPzn(detectedCodes[0]._data)),
        )
      ) {
        scannedPzn.value = "alreadyScanned";
      } else {
        scannedPzn.value = Pzn.getPzn(detectedCodes[0]._data);
        props.form.medication.pzns.push(scannedPzn.value);
      }
    } else {
      scannedPzn.value = null;
    }

    paused.value = true;
  };

  const wrongPznScanned = () => {
    props.form.medication.pzns.pop();
    paused.value = false;
  };

  defineExpose({
    open,
    close,
  });
</script>

<template>
  <PatientinputComponentScannerDialog
    ref="refScannerContainer"
    dialog-title="Medikament (PZN) scannen"
    dialog-type="abort"
    :formats="formats"
    loader-class="aspect-video"
    scanner-class="aspect-video"
    :paused="paused"
    :test-id="getTestId('pzn-scanner')"
    @detect="onDetect"
    @ok-button-clicked="$emit('ready')"
  >
    <p>Bitte scannen Sie den Strichcode Ihres Medikaments.</p>

    <template #paused>
      <ComponentAlert
        v-if="scannedPzn && scannedPzn !== 'alreadyScanned'"
        type="succes"
        class="mx-3 mb-0! w-full"
        :test-id="getTestId('pzn-scanner-dialog')"
      >
        <p>
          PZN
          <span class="font-mono">{{ scannedPzn }}</span>
          wurde erkannt.
        </p>

        <p class="mt-2">
          <MedButton
            variant="primary"
            :data-test="getTestId('pzn-scanner-dialog-continue-scan-button')"
            @click="paused = false"
          >
            Weiterscannen
          </MedButton>
        </p>

        <p class="mt-2">
          <MedButton data-test="getTestId('pzn-scanner-dialog-wrong-pzn-scanned')-button" @click="wrongPznScanned">
            Falsch? Erneut versuchen
          </MedButton>
        </p>
      </ComponentAlert>

      <ComponentAlert
        v-else-if="scannedPzn === 'alreadyScanned'"
        type="info"
        class="mx-3 mb-0! w-full"
        :test-id="getTestId('pzn-scanner-dialog-already-scanned')"
      >
        <p class="mb-2">Sie haben dieses Medikament bereits gescannt bzw. in Ihrem Medikationsplan.</p>

        <p>
          <component-button
            class="p4umc-primary"
            :test-id="getTestId('pzn-scanner-dialog-continue-scan')"
            @click="paused = false"
          >
            Weiterscannen
          </component-button>
        </p>
      </ComponentAlert>

      <ComponentAlert v-else type="error" class="mx-3 mb-0! w-full" :test-id="getTestId('pzn-scanner-dialog-else')">
        <p class="mb-2">Es wurde keine gültige PZN erkannt.</p>

        <p>
          <component-button
            class="p4umc-primary"
            :test-id="getTestId('pzn-scanner-dialog-scan-again')"
            @click="paused = false"
          >
            Erneut versuchen
          </component-button>
        </p>
      </ComponentAlert>
    </template>
  </PatientinputComponentScannerDialog>
</template>
